import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import './init-global';
import './index.css';
import './print.css';
import '@frontend/i18n';
import { getUser } from '@frontend/auth-helpers';
import { isLocalDeployment } from '@frontend/env';
import { http } from '@frontend/fetch';
import { sentry } from '@frontend/tracking';
import Root from './root';
import {
  disableLocalDebugSentry,
  enableLocalDebugSentry,
  getSentryConfig,
  SENTRY_ENVIRONMENT_KEY,
  SENTRY_LOCAL_ENABLE_KEY,
  shouldInitializeSentry,
} from './third-party-scripts/sentry-config';

declare global {
  interface Window {
    http?: typeof http;
    weaveSentry?: {
      enableLocalDebug: typeof enableLocalDebugSentry;
      disableLocalDebug: typeof disableLocalDebugSentry;
    };
  }
}

const shouldExposeWindowDebugUtils = import.meta.env.DEV || isLocalDeployment();

if (shouldExposeWindowDebugUtils) {
  window.http = http;
  window.weaveSentry = {
    enableLocalDebug: enableLocalDebugSentry,
    disableLocalDebug: disableLocalDebugSentry,
  };
}

if (shouldInitializeSentry()) {
  try {
    sentry.init(getSentryConfig());
    const userId = getUser()?.userID;
    // set the global sentry user so that any errors that occur after this point will be associated with the user who just logged in
    if (userId) sentry.instance.setUser({ id: userId });
    sentry.instance.addEventProcessor((event, hint) => {
      if (
        hint.originalException &&
        hint.originalException instanceof Error &&
        hint.originalException.message.match(/Failed to fetch data/i)
      ) {
        event.fingerprint = ['fetch-type-error'];
        event.tags = {
          topic: 'http',
        };
        event.contexts = {
          ...event.contexts,
          fetchData: {
            url: hint.originalException.message.split(': ')[1],
          },
        };
      }
      return event;
    });
  } catch (e) {
    console.error(e);
  }
} else {
  console.warn(
    `Sentry is not initialized because the app is not running in production. For local manual validation, call window.weaveSentry?.enableLocalDebug() or set sessionStorage['${SENTRY_LOCAL_ENABLE_KEY}']='true' and sessionStorage['${SENTRY_ENVIRONMENT_KEY}']='debug', then reload.`
  );
}

const renderApp = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = createRoot(document.getElementById('root')!);
  const disableStrictMode = import.meta.env.DISABLE_STRICT_MODE; // NOTE strict mode only affects dev builds and is compiled out of prod react builds (based on NODE_ENV var)
  if (disableStrictMode) {
    console.warn('React Strict Mode is disabled');
  }

  const app = (
    <Suspense fallback='loading'>
      <Root />
    </Suspense>
  );

  root.render(disableStrictMode ? app : <StrictMode>{app}</StrictMode>);
};

renderApp();

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.ready
    .then(() => console.log('Service Worker is active'))
    .catch((error) => console.error('Service Worker ready failed:', error));

  const swUrl =
    import.meta.env.MODE === 'test'
      ? '/mockSoftphoneServiceWorker.js'
      : import.meta.env.DEV || isLocalDeployment()
      ? '/dev-sw.js?dev-sw'
      : '/service-worker.js';

  navigator.serviceWorker.register(swUrl).catch((error) => console.error('Service Worker registration failed:', error));
}
